import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BRAND_NAME } from '../../constants/brand';

const SITE_URL = process.env.REACT_APP_PUBLIC_URL || 'https://novelday.net';

const DEFAULT_DESCRIPTION =
  'Discover, read, and share captivating web novels and light novels. Bookmark your favorites, track reading progress, and join a community of readers.';

const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image,
  url,
  type = 'website',
  noindex = false,
  keywords,
  // Optional structured data: a single schema.org object (e.g. Book, Article,
  // Person) or an array of them. Each is rendered as a separate
  // <script type="application/ld+json"> tag so Google can pick up rich-result
  // eligibility (star ratings, book carousel, breadcrumbs, author panel).
  jsonLd,
  // Optional alternate link tags (e.g. RSS feeds). Array of {rel, type, title, href}.
  alternateLinks,
}) => {
  const fullTitle = title
    ? `${title} | ${BRAND_NAME}`
    : `${BRAND_NAME} — Read Web Novels & Light Novels Online`;

  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const ogImage = image || `${SITE_URL}/branding/og-image.png`;

  const jsonLdBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const altLinks = Array.isArray(alternateLinks) ? alternateLinks : alternateLinks ? [alternateLinks] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={BRAND_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data (JSON-LD) for rich results */}
      {jsonLdBlocks.map((block, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}

      {/* Alternate link tags (RSS feeds, etc.) */}
      {altLinks.map((link, index) => (
        <link
          key={`alt-${index}`}
          rel={link.rel || 'alternate'}
          type={link.type}
          title={link.title}
          href={link.href}
        />
      ))}
    </Helmet>
  );
};

export default Seo;
